import { motion } from "framer-motion";
import { XCircle, CheckCircle2 } from "lucide-react";
import MovingBackground from "./MovingBackground";

const ease = [0.16, 1, 0.3, 1] as const;

const withoutItems = [
  "Uncontrolled AI sprawl & shadow‑AI",
  "Data leakage & compliance risks",
  "No ROI visibility or adoption metrics",
  "Siloed, inconsistent deployments",
  "Reactive security posture",
];

const withItems = [
  "Governed Copilot & agent rollout",
  "DLP, labelling & zero‑trust security",
  "Clear OKRs with measurable ROI",
  "Unified strategy across departments",
  "Proactive threat management",
];

const ComparisonSlide = ({ thumbnail }: { thumbnail?: boolean }) => (
  <div className="relative w-full h-full flex flex-col items-center justify-center bg-background overflow-hidden">
    <MovingBackground variant="mesh" />

    <div className="relative z-10 w-full max-w-5xl px-8">
      <motion.div
        className="text-center mb-6 md:mb-10"
        initial={thumbnail ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease }}
      >
        <p className="label-text mb-2" style={{ fontSize: thumbnail ? "0.5rem" : undefined }}>
          The Difference
        </p>
        <h2
          className="font-display font-bold text-foreground"
          style={{ fontSize: thumbnail ? "0.9rem" : "clamp(1.5rem, 3vw, 2.5rem)" }}
        >
          Without vs. With <span className="text-gradient">EastWest IT</span>
        </h2>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-4 md:gap-8">
        {/* Without */}
        <motion.div
          className="bg-card/80 backdrop-blur-sm rounded-xl p-5 md:p-8 border border-destructive/20"
          initial={thumbnail ? false : { opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.6, ease }}
        >
          <p
            className="font-display font-bold text-destructive mb-4"
            style={{ fontSize: thumbnail ? "0.6rem" : "1.1rem" }}
          >
            Without Guidance
          </p>
          <ul className="space-y-3">
            {withoutItems.map((item, i) => (
              <motion.li
                key={i}
                className="flex items-start gap-3 text-muted-foreground"
                style={{ fontSize: thumbnail ? "0.4rem" : "0.9rem" }}
                initial={thumbnail ? false : { opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.3, ease }}
              >
                {!thumbnail && <XCircle size={16} className="text-destructive shrink-0 mt-0.5" />}
                {item}
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* With */}
        <motion.div
          className="bg-card/80 backdrop-blur-sm rounded-xl p-5 md:p-8 border border-border/50"
          style={{ borderColor: "hsl(var(--success) / 0.2)" }}
          initial={thumbnail ? false : { opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.6, ease }}
        >
          <p
            className="font-display font-bold mb-4"
            style={{ fontSize: thumbnail ? "0.6rem" : "1.1rem", color: "hsl(var(--success))" }}
          >
            With EastWest IT
          </p>
          <ul className="space-y-3">
            {withItems.map((item, i) => (
              <motion.li
                key={i}
                className="flex items-start gap-3 text-muted-foreground"
                style={{ fontSize: thumbnail ? "0.4rem" : "0.9rem" }}
                initial={thumbnail ? false : { opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.3, ease }}
              >
                {!thumbnail && <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--success))" }} />}
                {item}
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>
    </div>
  </div>
);

export default ComparisonSlide;
